import { SelectBoxInput } from '@aqarmap/aqarmap-ui';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SectionEnum } from '~/constants/enums/listingEnums';
import {
  addListingDetailsType,
  CustomFieldsType,
  ListingDetailsType,
} from '~addListing/types';
import { handleAttributeChange } from '~addListing/utils/handleAttributeChange';

const BuildYear = ({
  customFieldsData,
  listing,
  errorAttributeIds,
  setErrorAttributeIds,
}: {
  customFieldsData: { attributes: CustomFieldsType[] };
  listing: ListingDetailsType;
  errorAttributeIds: number[];
  setErrorAttributeIds: React.Dispatch<React.SetStateAction<number[]>>;
}) => {
  const buildYearKey = 'year-built';
  const { t } = useTranslation('app');
  const listingPropertyTypeId = listing?.property_type.id;
  const isForRent = listing?.section.slug === SectionEnum.FORRENT;
  const [open, setOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState<string | null>(null);
  const [isError, setIsError] = React.useState(false);
  const buildYearData = customFieldsData.attributes?.find(
    (field: CustomFieldsType) => field.name === buildYearKey,
  );
  const buildYearId = Number(buildYearData?.id);

  const renderBuildYear = Object.values(
    buildYearData?.virtualPropertyTypesIds || {},
  ).includes(listingPropertyTypeId);

  const { setValue, getValues } = useFormContext<addListingDetailsType>();
  const defaultBuildYearValue =
    listing?.attributes.find((att) => att.custom_field.id === buildYearId)
      ?.value || 0;

  if (!renderBuildYear || isForRent) return null;

  useEffect(() => {
    setValue(
      'attributes',
      handleAttributeChange(
        getValues('attributes'),
        buildYearId,
        defaultBuildYearValue,
      ),
    );
  }, [isError]);

  useEffect(() => {
    setSelectedValue(`${defaultBuildYearValue}`);
  }, []);

  useEffect(() => {
    setIsError(errorAttributeIds.includes(buildYearId));
  }, [errorAttributeIds]);

  useEffect(() => {
    if (open) setIsError(false);
  }, [open]);

  return (
    <div>
      <SelectBoxInput
        label={{
          text: t('buildYearOrDeliveryYear'),
          className: '!text-title_4 text-black',
        }}
        field={{
          placeholder:
            selectedValue && selectedValue !== '0'
              ? selectedValue
              : t('buildYearOrDeliveryYearPlaceholder') || '',
          className: `text-body_1 ${selectedValue ? 'text-gray__dark_2' : 'text-gray__dark_1'}`,
        }}
        open={open}
        setOpen={setOpen}
        error={isError ? t('buildYearError') : ''}
      >
        <div className="flex flex-col max-h-[300px] overflow-y-auto">
          {buildYearData?.virtualOptions.choices.map((option) => (
            <div
              key={option.value}
              className="flex items-center gap-2x py-x px-1.5x cursor-pointer hover:bg-gray_light_1"
              onClick={() => {
                setSelectedValue(option.id);
                setValue(
                  'attributes',
                  handleAttributeChange(
                    getValues('attributes'),
                    buildYearId,
                    option.id,
                  ),
                );
                setOpen(false);
                setErrorAttributeIds((prev) =>
                  prev.filter((id) => id !== buildYearId),
                );
              }}
            >
              <span>{option.value}</span>
            </div>
          ))}
        </div>
      </SelectBoxInput>
    </div>
  );
};

export default BuildYear;
